import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { PARCEL_LAYERS_MIN_ZOOM } from '../core/constants';
import { centroidOfGeometry } from '../utils/coords';
import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';

export const PARCEL_BASE_STYLE = {
    color: 'rgba(63,98,18,0.34)',
    weight: 1,
    fillColor: 'transparent',
    fillOpacity: 0,
    interactive: false
};

export const PARCEL_LABEL_STYLE = {
    layout: {
        'text-field': ['get', 'numer'],
        'text-size': 11,
        'text-allow-overlap': false,
        'text-ignore-placement': false
    },
    paint: {
        'text-color': 'rgba(120,53,15,0.85)',
        'text-halo-color': 'rgba(255,255,255,0.9)',
        'text-halo-width': 1.5
    }
};

export const PARCEL_SELECTED_STYLE = {
    color: '#f97316',
    weight: 3.4,
    fillColor: 'rgba(249,115,22,0.14)',
    fillOpacity: 1,
    opacity: 1
};

function getFeatureCollectionCenter(data) {
    if (!data || !Array.isArray(data.features) || !data.features.length) {
        return null;
    }

    try {
        const bounds = new maplibregl.LngLatBounds();
        let hasBounds = false;

        data.features.forEach((feature) => {
            if (!feature || !feature.geometry) {
                return;
            }

            const center = centroidOfGeometry(feature.geometry);
            if (!center) {
                return;
            }

            bounds.extend(center);
            hasBounds = true;
        });

        return hasBounds ? {
            lat: bounds.getCenter().lat,
            lng: bounds.getCenter().lng
        } : null;
    } catch (_error) {
        return null;
    }
}

export default class DzialkiLayer extends GeoJsonFetchLayer {
    static ID = 'dzialki';
    static DESCRIPTION = 'Działki ewidencyjne z Ewidencji Gruntów i Budynków';
    static MIN_ZOOM = PARCEL_LAYERS_MIN_ZOOM;
    static SOURCE_ID = 'src-dzialki';
    static BASE_LAYER_IDS = ['lyr-dzialki-line', 'lyr-dzialki-label'];
    static SELECTED_SOURCE_ID = 'src-selected-dzialka';
    static SELECTED_LAYER_IDS = ['lyr-selected-dzialka-fill', 'lyr-selected-dzialka-line'];
    static RENDER_ORDER_LAYER_IDS = DzialkiLayer.BASE_LAYER_IDS.concat(DzialkiLayer.SELECTED_LAYER_IDS);

    getSourceId() {
        return DzialkiLayer.SOURCE_ID;
    }

    getLayerIds() {
        return DzialkiLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return DzialkiLayer.RENDER_ORDER_LAYER_IDS;
    }

    buildUrl() {
        return 'api/dzialki?bbox=' + this.config.getCurrentBbox()
            + '&zoom=' + this.config.getCurrentZoom()
            + '&background=1';
    }

    getFeatureKey(feature) {
        const props = feature && feature.properties ? feature.properties : {};
        if (props.id != null && props.id !== '') {
            return props.id;
        }
        if (props.id_dzialki != null && props.id_dzialki !== '') {
            return props.id_dzialki;
        }
        return this.featureGeometryKey(feature);
    }

    _addBaseLayers(map, sourceId) {
        map.addLayer({
            id: DzialkiLayer.BASE_LAYER_IDS[0],
            type: 'line',
            source: sourceId,
            paint: {
                'line-color': PARCEL_BASE_STYLE.color,
                'line-width': PARCEL_BASE_STYLE.weight
            }
        });
        map.addLayer({
            id: DzialkiLayer.BASE_LAYER_IDS[1],
            type: 'symbol',
            source: sourceId,
            minzoom: 18,
            layout: PARCEL_LABEL_STYLE.layout,
            paint: PARCEL_LABEL_STYLE.paint
        });
    }

    renderData(data) {
        const map = this.getMap();
        if (!map) {
            return null;
        }

        return this.renderSourceData(data, this._addBaseLayers.bind(this));
    }

    clearSelectedFeature() {
        const map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, DzialkiLayer.SELECTED_SOURCE_ID, DzialkiLayer.SELECTED_LAYER_IDS);
    }

    renderSelectedFeature(data) {
        const map = this.getMap();
        if (!map || !data || !Array.isArray(data.features) || !data.features.length) {
            this.clearSelectedFeature();
            return null;
        }

        this.clearSelectedFeature();

        addOrUpdateGeoJsonSource(map, DzialkiLayer.SELECTED_SOURCE_ID, data);
        map.addLayer({
            id: DzialkiLayer.SELECTED_LAYER_IDS[0],
            type: 'fill',
            source: DzialkiLayer.SELECTED_SOURCE_ID,
            paint: {
                'fill-color': PARCEL_SELECTED_STYLE.fillColor,
                'fill-opacity': PARCEL_SELECTED_STYLE.fillOpacity
            }
        });
        map.addLayer({
            id: DzialkiLayer.SELECTED_LAYER_IDS[1],
            type: 'line',
            source: DzialkiLayer.SELECTED_SOURCE_ID,
            paint: {
                'line-color': PARCEL_SELECTED_STYLE.color,
                'line-width': PARCEL_SELECTED_STYLE.weight
            }
        });

        return {
            layer: DzialkiLayer.SELECTED_SOURCE_ID,
            center: getFeatureCollectionCenter(data)
        };
    }
}
